import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  carregarIa,
  PROVEDORES_IA,
  salvarChaveIa,
  testarIa,
  usarProvedorIa,
  type ProvedorIa,
  type SituacaoIa,
  type TesteIa,
} from '../api/admin'
import { UsoIaCard } from '../components/UsoIaCard'

const dataCurta = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })

/**
 * Qual IA escreve as leituras dos painéis, com qual modelo e qual chave. Cada provedor
 * guarda a sua chave (trocar de provedor não apaga a outra). A chave vai para o cofre do
 * Supabase e nunca volta para a tela — só os 4 últimos caracteres.
 */
export function IaPage() {
  const [situacao, setSituacao] = useState<SituacaoIa | null>(null)
  const [escolhido, setEscolhido] = useState<ProvedorIa>('anthropic')
  const [modelo, setModelo] = useState('')
  const [chave, setChave] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [removendo, setRemovendo] = useState(false)
  const [testando, setTestando] = useState(false)
  const [teste, setTeste] = useState<TesteIa | null>(null)

  const carregar = useCallback(async () => {
    try {
      const s = await carregarIa()
      setSituacao(s)
      return s
    } catch (e) {
      setErro((e as Error).message)
      return null
    }
  }, [])

  useEffect(() => {
    carregar().then((s) => {
      if (!s) return
      setEscolhido(s.provedor)
      setModelo(s.modelo ?? '')
    })
  }, [carregar])

  if (!situacao) {
    return erro ? (
      <Alert severity="error">{erro}</Alert>
    ) : (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  const info = PROVEDORES_IA.find((p) => p.id === escolhido)!
  const emUso = PROVEDORES_IA.find((p) => p.id === situacao.provedor)!
  const chaveDe = (p: ProvedorIa) => situacao.chaves.find((k) => k.provedor === p)
  const chaveAtual = chaveDe(escolhido)
  const modeloLimpo = modelo.trim()
  const modeloFinal = !modeloLimpo || modeloLimpo === info.modeloPadrao ? null : modeloLimpo
  const mudouProvedor = escolhido !== situacao.provedor || modeloFinal !== situacao.modelo
  const podeSalvar = !salvando && (chave.trim() !== '' || mudouProvedor)

  const escolher = (p: ProvedorIa) => {
    setEscolhido(p)
    setModelo(p === situacao.provedor ? (situacao.modelo ?? '') : '')
    setChave('')
    setTeste(null)
  }

  const salvar = async () => {
    setSalvando(true)
    setErro(null)
    setAviso(null)
    setTeste(null)
    try {
      if (chave.trim()) await salvarChaveIa(escolhido, chave)
      await usarProvedorIa(escolhido, modeloFinal)
      setChave('')
      setAviso(`${info.nome} (${info.produto}) em uso, com o modelo ${modeloFinal ?? info.modeloPadrao}.`)
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      await carregar()
      setSalvando(false)
    }
  }

  const remover = async () => {
    setRemovendo(false)
    setSalvando(true)
    setErro(null)
    setAviso(null)
    try {
      await salvarChaveIa(escolhido, '')
      setAviso(`Chave da ${info.nome} removida.`)
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      await carregar()
      setSalvando(false)
    }
  }

  const testar = async () => {
    setTestando(true)
    setTeste(null)
    setErro(null)
    try {
      setTeste(await testarIa())
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setTestando(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, lineHeight: 1.2 }}>
          IA
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
          Escolha qual IA escreve as leituras dos painéis e cadastre a chave de API. A chave fica criptografada no cofre do
          Supabase e não volta para a tela — aparecem só os 4 últimos caracteres. Cada provedor guarda a sua: trocar de
          provedor não apaga a do outro. Sem chave, os painéis mostram a leitura automática.
        </Typography>
      </Box>

      {erro ? (
        <Alert severity="error" onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}
      {aviso ? (
        <Alert severity="success" onClose={() => setAviso(null)}>
          {aviso}
        </Alert>
      ) : null}
      {!chaveDe(situacao.provedor) ? (
        <Alert severity="info">
          A IA em uso ({emUso.nome} · {emUso.produto}) está sem chave: os painéis mostram só a leitura automática.
        </Alert>
      ) : null}

      <Box
        role="radiogroup"
        aria-label="Provedor de IA"
        sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}
      >
        {PROVEDORES_IA.map((p) => {
          const ativo = p.id === escolhido
          const k = chaveDe(p.id)
          return (
            <ButtonBase
              key={p.id}
              role="radio"
              aria-checked={ativo}
              onClick={() => escolher(p.id)}
              sx={{ display: 'block', textAlign: 'left', borderRadius: 1 }}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  height: '100%',
                  borderWidth: 2,
                  borderColor: ativo ? 'primary.main' : 'divider',
                  transition: 'border-color 160ms ease',
                }}
              >
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {p.produto}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {p.nome}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  {p.id === situacao.provedor ? <Chip size="small" color="primary" label="Em uso" /> : null}
                  <Chip
                    size="small"
                    variant="outlined"
                    color={k ? 'success' : 'default'}
                    label={k ? `Chave ••••${k.final}` : 'Sem chave'}
                  />
                </Stack>
              </Paper>
            </ButtonBase>
          )
        })}
      </Box>

      <Paper variant="outlined" sx={{ p: 2.5, opacity: salvando ? 0.6 : 1, transition: 'opacity 160ms ease' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          {info.produto} ({info.nome})
        </Typography>
        <Stack spacing={2} sx={{ maxWidth: 560 }}>
          <Autocomplete
            freeSolo
            options={info.sugestoes}
            inputValue={modelo}
            onInputChange={(_e, v) => setModelo(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Modelo"
                placeholder={info.modeloPadrao}
                helperText={`Em branco, usa o padrão (${info.modeloPadrao}). Dá para digitar outro nome de modelo do provedor.`}
              />
            )}
          />
          <TextField
            label="Chave de API"
            type="password"
            autoComplete="off"
            value={chave}
            onChange={(e) => setChave(e.target.value)}
            placeholder={chaveAtual ? `Cadastrada: ••••${chaveAtual.final} — cole outra para trocar` : 'Cole aqui a chave'}
            helperText={
              <>
                {chaveAtual ? `Chave terminando em ${chaveAtual.final}, cadastrada em ${dataCurta(chaveAtual.atualizada_em)}. ` : null}
                {info.dica}{' '}
                <Link href={info.ondeCriar} target="_blank" rel="noopener noreferrer">
                  Criar a chave
                </Link>
              </>
            }
          />
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button variant="contained" disabled={!podeSalvar} onClick={salvar} sx={{ textTransform: 'none' }}>
              {escolhido === situacao.provedor ? 'Salvar' : `Salvar e usar ${info.produto}`}
            </Button>
            {chaveAtual ? (
              <Button color="error" disabled={salvando} onClick={() => setRemovendo(true)} sx={{ textTransform: 'none' }}>
                Remover chave
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Paper>

      <UsoIaCard
        key={escolhido}
        provedor={escolhido}
        produto={info.produto}
        nome={info.nome}
        modeloAtual={escolhido === situacao.provedor ? situacao.modelo || info.modeloPadrao : ''}
        ondeVerGasto={info.ondeVerGasto}
      />

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
          <Box sx={{ mr: 'auto', maxWidth: 640 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Testar a IA em uso
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pede à {emUso.nome} uma leitura de exemplo, com números fictícios, usando a configuração salva. É uma chamada
              real, de custo baixo.
            </Typography>
          </Box>
          <Button variant="outlined" disabled={testando || salvando} onClick={testar} sx={{ textTransform: 'none' }}>
            {testando ? 'Testando…' : 'Testar'}
          </Button>
        </Stack>
        {teste ? (
          <Alert severity={!teste.ok ? 'error' : teste.conferida ? 'success' : 'warning'} sx={{ mt: 2 }}>
            {teste.ok ? (
              <>
                <strong>
                  Funcionando: {teste.provedor} · {teste.modelo}.
                </strong>{' '}
                {teste.conferida
                  ? 'Os números do exemplo passaram na conferência.'
                  : 'Respondeu, mas citou um número que não estava nos achados — nos painéis essa leitura seria descartada. Vale testar outro modelo.'}
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  “{teste.exemplo}”
                </Typography>
              </>
            ) : (
              <>
                <strong>
                  Não funcionou ({teste.provedor} · {teste.modelo}):
                </strong>{' '}
                {teste.motivo}.
              </>
            )}
          </Alert>
        ) : null}
        {teste?.modelos?.length ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Modelos disponíveis nesta chave ({teste.modelos.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Segundo a {teste.provedor}. Clique para usar, depois em Salvar e Testar de novo.
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {teste.modelos.map((m) => (
                <Chip
                  key={m}
                  size="small"
                  label={m}
                  variant={m === (situacao.modelo ?? emUso.modeloPadrao) ? 'filled' : 'outlined'}
                  color={m === (situacao.modelo ?? emUso.modeloPadrao) ? 'primary' : 'default'}
                  onClick={() => {
                    setEscolhido(situacao.provedor)
                    setModelo(m)
                    setChave('')
                  }}
                />
              ))}
            </Stack>
          </Box>
        ) : null}
      </Paper>

      <Dialog open={removendo} onClose={() => setRemovendo(false)}>
        <DialogTitle>Remover a chave da {info.nome}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A chave sai do cofre do Supabase.
            {escolhido === situacao.provedor ? ' Como este é o provedor em uso, os painéis passam a mostrar só a leitura automática.' : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovendo(false)} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button color="error" onClick={remover} sx={{ textTransform: 'none' }}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
